import React, { useEffect, useRef, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import IMG from "../../../constants/image";

const NestedSelect = ({ id, title, required, data, viewOnly, value, placeholder, name, onChange, error }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectData, setSelectData] = useState(data)
    const dropdownRef = useRef(null);

    useEffect(() => {
        setSelectData(remakeData(data))
    }, [data])

    const remakeData = (newData) => {
        return newData.map(item => ({
            ...item,
            children: item?.category_children || []
        }))
    }

    const [expandedItems, setExpandedItems] = useState([]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const findItemRecursive = (items, targetId) => {
        for (const item of items) {
            if ((item.id || '').toLowerCase() === (targetId || '').toLowerCase()) {
                return item;
            }
            if (item.children && item.children.length > 0) {
                const found = findItemRecursive(item.children, targetId);
                if (found) return found;
            }
        }
        return null;
    };

    useEffect(() => {
        if (value && selectData?.length > 0) {
            const item = findItemRecursive(selectData, value);
            setSelectedItem(item);
        } else {
            setSelectedItem(null);
        }
    }, [value, selectData]);

    const hasSelectedChild = (item) => {
        if (!selectedItem || !item.children) return false;
        return item.children.some(child =>
            child.id === selectedItem.id || hasSelectedChild(child)
        );
    };

    const toggleExpand = (itemId) => {
        setExpandedItems((prev) =>
            prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId]
        );
    };

    const handleSelect = (item) => {
        if (item.children && item.children.length > 0) {
            toggleExpand(item.id);
        } else {
            onChange && onChange({ target: { name, value: item.id } });
            setSelectedItem(item);
            setIsOpen(false);
        }
    };

    if (viewOnly) {
        return (
            <div className='text-sm w-full'>
                {title && <p className='mb-1'>{title}</p>}
                <p className='text-[#9E9E9E] py-2'>{value}</p>
            </div>
        );
    }


    const renderItems = (items, level = 0) => {
        return items.map((item) => {
            if (item?.metadata?.display_cms === 'no') return null;
            return (
                <div key={item.id} style={{ paddingLeft: `${level * 16}px` }}>
                    <div
                        className={`flex items-center p-2 cursor-pointer transition-all duration-200 ${selectedItem?.id === item.id
                            ? "text-green-700"
                            : "hover:bg-gray-100 text-gray-700"
                            } ${hasSelectedChild(item) ? "font-bold text-black" : ""}`}
                        onClick={(e) => {
                            e?.preventDefault()
                            handleSelect(item)
                        }}
                        role="option"
                        aria-selected={selectedItem?.id === item.id}
                        tabIndex="0"
                    >
                        <span className="flex-1 ml-5">{item.name}</span>
                        {item.children && item.children?.length > 0 && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleExpand(item.id);
                                }}
                                className="p-1 mr-2 hover:bg-gray-200 rounded-full transition-transform duration-200"
                                aria-expanded={expandedItems.includes(item.id)}
                            >
                                {expandedItems.includes(item.id) ? (
                                    <FiChevronDown className="text-gray-600" />
                                ) : (
                                    <FiChevronRight className="text-gray-600" />
                                )}
                            </button>
                        )}
                    </div>
                    {item.children && expandedItems.includes(item.id) && (
                        <div className="transform transition-all duration-200 ease-in-out">
                            {renderItems(item.children, level + 1)}
                        </div>
                    )}
                </div>
            )
        });
    };

    return (
        <div id={id} className="w-full max-w-md text-sm" ref={dropdownRef}>
            {title} {required && <span className='text-[#E4626F]'>*</span>}
            <div className="relative mt-1">
                <button
                    onClick={(e) => {
                        e.preventDefault()
                        setIsOpen(!isOpen)
                    }}
                    className="w-full p-2 text-left bg-white border border-gray-300 rounded-lg"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    <div className="flex items-center justify-between">
                        <span className={!selectedItem ? "text-[#D9D9D9]" : ""}>
                            {selectedItem ? selectedItem.name : placeholder}
                        </span>
                        <FiChevronDown
                            className={`transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""
                                }`}
                        />
                    </div>
                </button>

                {error && (
                    <div className="absolute bottom-[-22px] left-0 text-[#E4626F] text-xs flex items-center">
                        <img className='mr-1' src={IMG.icon.errorOutline} alt="errorOutline" />
                        {error}
                    </div>
                )}
                {isOpen && (
                    <div
                        className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div role="listbox" className="py-1">
                            {renderItems(selectData)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NestedSelect;
